import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

/**
 * SimpleItemModal - A reusable modal for creating/editing simple items
 * @param {Object} item - The item being edited (or empty for new)
 * @param {boolean} editMode - Whether editing an existing item
 * @param {boolean} open - Modal open state
 * @param {function} handleCancel - Cancel handler
 * @param {function} handleAddItem - Add new item handler
 * @param {function} handleUpdateItem - Update existing item handler
 * @param {string} title - Modal title (e.g., "Location")
 * @param {string} itemNameLabel - Label for the name field
 * @param {string} descriptionLabel - Label for the description field
 * @param {Object} checkboxConfig - Optional checkbox configuration
 * @param {string} checkboxConfig.name - Field name for the checkbox (e.g., "is_shop")
 * @param {string} checkboxConfig.label - Label displayed next to checkbox
 * @param {string} checkboxConfig.helperText - Helper text displayed below checkbox
 */
export const SimpleItemModal = ({
  item,
  editMode,
  open,
  handleCancel,
  handleAddItem,
  handleUpdateItem,
  title,
  itemNameLabel,
  descriptionLabel = 'Description',
  checkboxConfig = null,
}) => {
  const [newItem, setNewItem] = useState(item);

  useEffect(() => {
    if (item) {
      setNewItem(item);
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setNewItem((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleClose = () => {
    setNewItem({
      name: '',
      description: '',
    });
    handleCancel();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    editMode ? handleUpdateItem(newItem) : handleAddItem(newItem);

    setNewItem({
      name: '',
      description: '',
    });
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>{editMode ? `Edit ${title}` : `New ${title}`}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <TextField
            required
            autoFocus
            fullWidth
            margin="normal"
            id="name"
            label={itemNameLabel}
            name="name"
            autoComplete="off"
            type="text"
            value={newItem.name || ''}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            id="description"
            label={descriptionLabel}
            name="description"
            autoComplete="off"
            type="text"
            value={newItem.description || ''}
            onChange={handleChange}
            multiline
            rows={3}
          />

          {/* Optional Checkbox Field */}
          {checkboxConfig && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newItem[checkboxConfig.name] || false}
                    onChange={handleChange}
                    name={checkboxConfig.name}
                    color="primary"
                  />
                }
                label={checkboxConfig.label}
              />
              {checkboxConfig.helperText && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', ml: 4, mt: -0.5 }}
                >
                  {checkboxConfig.helperText}
                </Typography>
              )}
            </Box>
          )}

          <DialogActions
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <Button
              type="submit"
              variant="contained"
              sx={{ flex: 1, height: 36 }}
            >
              {editMode ? `Update ${title}` : `Add ${title}`}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ flex: 1, height: 36 }}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};
